import React from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'react-i18next';
import { toUpper } from 'lodash';
import numeral from 'numeral';
import { GlobalToken } from 'antd';

import Super from '@/comp/Super';
import { useMeasurement } from '@/hooks/useMeasurement';
import { formatThousands } from '@/utils/util-functions';
import IconSavingsV from '@/assets/icon-savings-v.svg?react';
import IconSavingsX from '@/assets/icon-savings-x.svg?react';

interface PowerTooltipInterface {
  value: number;
  specificPower: number;
  volume: number;
  name: string;
  measurementSystem: string;
  energySaving?: number;
  kWhToMoneyRate?: number;
  savingCurrency?: string;
  co2Saving?: number;
  token: GlobalToken;
}

const PowerTooltip = ({
  value,
  specificPower,
  volume,
  name,
  measurementSystem,
  energySaving,
  kWhToMoneyRate,
  savingCurrency,
  co2Saving,
  token,
}: PowerTooltipInterface) => {
  const { t } = useTranslation();
  const { tunit } = useMeasurement();
  const positiveEnergySavings = energySaving > 0;

  return (
    <TooltipContainer theme={{ token }}>
      <div className="title">{name}</div>

      <table>
        <tr className="first-row">
          <td>
            <div className="name">{t('Energy')}</div>
          </td>
          <td>
            <TooltipValueContainerTopRow theme={{ token }}>
              <span className="value">{formatThousands(value)}</span>
              <span className="label">{tunit('kWh')}</span>
            </TooltipValueContainerTopRow>
          </td>
        </tr>
        <tr className="spacer">
          <td></td>
          <td></td>
        </tr>
        <tr>
          <td>
            <div className="name">{t('Specific Power')}</div>
          </td>
          <td>
            <TooltipValueContainer theme={{ token }}>
              <span className="value">
                {toUpper(measurementSystem) === 'IMPERIAL'
                  ? numeral(specificPower).format('0,0.0')
                  : numeral(specificPower).format('0,0.00')}
              </span>
              <span className="label">
                <Trans i18nKey={tunit('kW/(m3/min)')}>
                  <Super />
                </Trans>
              </span>
            </TooltipValueContainer>
          </td>
        </tr>
        <tr>
          <td>
            <div className="name">{t('Volume')}</div>
          </td>
          <td>
            <TooltipValueContainer theme={{ token }}>
              <span className="value">{formatThousands(volume)}</span>
              <span className="label">
                <Trans i18nKey={tunit('m^3')}>
                  <Super />
                </Trans>
              </span>
            </TooltipValueContainer>
          </td>
        </tr>
        {energySaving !== undefined ? (
          <tr className="saving-row">
            <td>
              <div className="sign">{positiveEnergySavings ? <IconSavingsV /> : <IconSavingsX />}</div>

              <div className="name">{t('Energy Saving')}</div>
            </td>
            <td>
              <TooltipValueContainer positiveSavings={positiveEnergySavings} theme={{ token }}>
                <span className="value">{formatThousands(energySaving)}</span>
                <span className="label">{t('kWh')}</span>
              </TooltipValueContainer>
            </td>
          </tr>
        ) : null}
        {energySaving && kWhToMoneyRate && savingCurrency ? (
          <tr className="energy-saving-row">
            <td>
              <div className="name">{t('')}</div>
            </td>
            <td>
              <TooltipValueContainer positiveSavings={positiveEnergySavings} theme={{ token }}>
                <span className="value">{formatThousands(energySaving * kWhToMoneyRate)}</span>
                <span className="label"> {savingCurrency}</span>
              </TooltipValueContainer>
            </td>
          </tr>
        ) : null}
        {co2Saving ? (
          <tr className="co2-saving-row">
            <td>
              <div className="sign">{energySaving > 0 ? <IconSavingsV /> : <IconSavingsX />}</div>
              <div className="name">{t('CO2 Reduction')}</div>
            </td>
            <td>
              <TooltipValueContainer positiveSavings={positiveEnergySavings} theme={{ token }}>
                <span className="value">{numeral(co2Saving).format('0,0.0')}</span>
                <span className="label">{t('MT GHG')}</span>
              </TooltipValueContainer>
            </td>
          </tr>
        ) : null}
      </table>
    </TooltipContainer>
  );
};

interface TooltipValueContainerProps {
  theme: { token: GlobalToken };
  positiveSavings?: boolean;
}

const TooltipValueContainer = styled.div<TooltipValueContainerProps>`
  color: ${({ theme: { token }, positiveSavings }) =>
    positiveSavings === undefined ? token.colorText : positiveSavings ? token.colorSuccess : token.colorError};
  display: flex;
  .label {
    padding-left: ${({ theme: { token } }) => token.paddingXXS}px;
  }

  .value {
    width: 100px;
    padding-right: ${({ theme: { token } }) => token.paddingXXS}px;
  }
`;

const TooltipValueContainerTopRow = styled(TooltipValueContainer)<{ theme: { token: GlobalToken } }>`
  background-color: ${({ theme: { token } }) => token.colorBgElevated};
  color: ${({ theme: { token } }) => token.colorText};

  .value {
    font-size: ${({ theme: { token } }) => token.fontSize}px;
    font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
  }
`;

const TooltipContainer = styled.div<{ theme: { token: GlobalToken } }>`
  position: relative;
  padding: ${({ theme: { token } }) => token.paddingMD}px ${({ theme: { token } }) => token.paddingLG}px;
  background-color: ${({ theme: { token } }) => token.colorBgElevated};
  color: ${({ theme: { token } }) => token.colorText};
  border-radius: 4px;
  box-shadow: ${({ theme: { token } }) => token.boxShadowSecondary};

  .title {
    font-size: ${({ theme: { token } }) => token.fontSize}px;
    font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
    padding-bottom: ${({ theme: { token } }) => token.paddingSM}px;
  }

  table {
    tbody {
      font-size: ${({ theme: { token } }) => token.fontSize}px;
      font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
    }

    tr {
      padding: ${({ theme: { token } }) => token.paddingXXS}px 0;

      &.first-row {
        td {
          background-color: ${({ theme: { token } }) => token.colorBgElevated};

          .name {
            font-size: ${({ theme: { token } }) => token.fontSize}px;
            font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
          }

          &:first-child {
            border-radius: 2px 0 0 2px;
          }

          &:last-child {
            border-radius: 0 2px 2px 0;
          }
        }
      }

      td {
        position: relative;
        padding: ${({ theme: { token } }) => token.paddingXXS}px ${({ theme: { token } }) => token.paddingXS}px;

        .sign {
          position: absolute;
          left: -15px;
          top: 6px;
        }
      }
    }
  }
`;

export default PowerTooltip;
